import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, Landmark, Loader2, Lock, Smartphone, CreditCard, Wallet, X } from "lucide-react";
import { inr } from "@/lib/api";

const METHOD_META = {
  upi: { label: "UPI", icon: Smartphone, hint: "Google Pay · PhonePe · Paytm · BHIM" },
  card: { label: "Card", icon: CreditCard, hint: "Visa · Mastercard · RuPay · Amex" },
  netbanking: { label: "Net Banking", icon: Landmark, hint: "HDFC · ICICI · SBI · Axis · Kotak" },
  cod: { label: "COD", icon: Wallet, hint: "Pay cash on delivery" },
};

export default function PaymentModal({ open, method, amount, onClose, onSuccess }) {
  const [stage, setStage] = useState("details");
  const [upiId, setUpiId] = useState("");

  useEffect(() => {
    if (open) setStage("details");
  }, [open]);

  const meta = METHOD_META[method] || METHOD_META.upi;

  const pay = () => {
    setStage("processing");
    setTimeout(() => {
      setStage("done");
      setTimeout(onSuccess, 1400);
    }, 1900);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-[180] bg-black/75 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={stage === "details" ? onClose : undefined}
            data-testid="payment-overlay"
          />
          <motion.div
            data-testid="payment-modal"
            className="fixed bottom-0 left-1/2 z-[185] w-full max-w-md -translate-x-1/2 overflow-hidden rounded-t-3xl sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2 sm:rounded-3xl glass"
            style={{ background: "var(--mn-bg2)" }}
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 60 }}
            transition={{ type: "spring", stiffness: 280, damping: 28 }}
          >
            <div className="flex items-center justify-between border-b px-6 py-4" style={{ borderColor: "var(--mn-glass-border)" }}>
              <div>
                <p className="font-serif text-lg">Mother Nature</p>
                <p className="flex items-center gap-1 font-mono text-[0.6rem] uppercase tracking-[0.2em]" style={{ color: "var(--mn-text2)" }}>
                  <Lock size={10} /> Secured checkout · Razorpay (simulated)
                </p>
              </div>
              <div className="text-right">
                <p className="font-mono text-lg" style={{ color: "var(--mn-gold)" }} data-testid="payment-amount">{inr(amount)}</p>
                {stage === "details" && (
                  <button data-testid="payment-close" onClick={onClose} aria-label="Close payment" className="opacity-60 hover:opacity-100">
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>

            {stage === "details" && (
              <div className="p-6">
                <div className="flex items-center gap-3 rounded-2xl border p-4" style={{ borderColor: "var(--mn-glass-border)", background: "var(--mn-surface)" }}>
                  <meta.icon size={22} style={{ color: "var(--mn-gold)" }} />
                  <div>
                    <p className="text-sm font-medium" data-testid="payment-method-label">{meta.label}</p>
                    <p className="text-xs" style={{ color: "var(--mn-text2)" }}>{meta.hint}</p>
                  </div>
                </div>

                {method === "upi" && (
                  <div className="mt-4">
                    <div className="flex flex-wrap gap-2">
                      {["Google Pay", "PhonePe", "Paytm", "BHIM"].map((app) => (
                        <span key={app} className="rounded-full border px-3 py-1.5 font-mono text-[0.65rem]" style={{ borderColor: "var(--mn-glass-border)", color: "var(--mn-text2)" }}>
                          {app}
                        </span>
                      ))}
                    </div>
                    <input
                      data-testid="upi-id-input"
                      value={upiId}
                      onChange={(e) => setUpiId(e.target.value)}
                      placeholder="yourname@okgpay / @ybl / @paytm"
                      className="mt-3 w-full rounded-full border bg-transparent px-5 py-3 text-sm outline-none focus:border-[var(--mn-gold)]"
                      style={{ borderColor: "var(--mn-glass-border)" }}
                    />
                  </div>
                )}
                {method === "card" && (
                  <div className="mt-4 space-y-3">
                    <input data-testid="card-number-input" placeholder="Card number" maxLength={19} className="w-full rounded-full border bg-transparent px-5 py-3 text-sm outline-none focus:border-[var(--mn-gold)]" style={{ borderColor: "var(--mn-glass-border)" }} />
                    <div className="flex gap-3">
                      <input data-testid="card-expiry-input" placeholder="MM/YY" maxLength={5} className="w-1/2 rounded-full border bg-transparent px-5 py-3 text-sm outline-none focus:border-[var(--mn-gold)]" style={{ borderColor: "var(--mn-glass-border)" }} />
                      <input data-testid="card-cvv-input" placeholder="CVV" maxLength={4} type="password" className="w-1/2 rounded-full border bg-transparent px-5 py-3 text-sm outline-none focus:border-[var(--mn-gold)]" style={{ borderColor: "var(--mn-glass-border)" }} />
                    </div>
                  </div>
                )}
                {method === "netbanking" && (
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    {["HDFC Bank", "ICICI Bank", "SBI", "Axis Bank"].map((b) => (
                      <label key={b} className="flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2.5 text-xs transition-colors hover:border-[var(--mn-gold)]" style={{ borderColor: "var(--mn-glass-border)" }}>
                        <input type="radio" name="bank" className="accent-[#D4AF37]" data-testid={`bank-${b.toLowerCase().replace(/\s/g, "-")}`} /> {b}
                      </label>
                    ))}
                  </div>
                )}

                <button
                  data-testid="payment-pay-button"
                  onClick={pay}
                  className="btn-gold mt-6 flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-sm"
                >
                  <Lock size={14} /> Pay {inr(amount)}
                </button>
                <p className="mt-3 text-center font-mono text-[0.6rem] uppercase tracking-[0.2em]" style={{ color: "var(--mn-text2)" }}>
                  256-bit encrypted · Demo mode, no real charge
                </p>
              </div>
            )}

            {stage === "processing" && (
              <div className="flex flex-col items-center px-6 py-14" data-testid="payment-processing">
                <Loader2 size={40} className="animate-spin" style={{ color: "var(--mn-gold)" }} />
                <p className="mt-5 font-serif text-lg">Talking to your bank…</p>
                <p className="mt-1 text-sm" style={{ color: "var(--mn-text2)" }}>Do not press back or refresh.</p>
              </div>
            )}

            {stage === "done" && (
              <div className="flex flex-col items-center px-6 py-14" data-testid="payment-success">
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 260, damping: 16 }}>
                  <CheckCircle2 size={56} style={{ color: "var(--mn-mint)" }} />
                </motion.div>
                <p className="mt-5 font-serif text-xl">Payment successful</p>
                <p className="mt-1 text-sm" style={{ color: "var(--mn-text2)" }}>Placing your order…</p>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
